/**
 * ClientViewHeader — Back arrow, client name title, consent-level pill.
 *
 * Back button: 44×44 touch target (WCAG AA).
 * Consent pill: display only — no tap target required.
 * Contrast: #0D9488 on rgba(20,184,166,0.12) ≈ 4.9:1 (AA).
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';

type ConsentLevel = 'scores_and_patterns' | 'full_history' | 'original_text';

interface ClientViewHeaderProps {
  clientName: string;
  consentLevel: ConsentLevel;
  onBack: () => void;
  t: TFunction<'practitioner'>;
}

const HIT_SLOP = { top: 4, right: 4, bottom: 4, left: 4 } as const;

const CONSENT_LABEL_KEYS: Record<ConsentLevel, string> = {
  scores_and_patterns: 'consent.scores_and_patterns',
  full_history:        'consent.full_history',
  original_text:       'consent.original_text',
};

export const ClientViewHeader: React.FC<ClientViewHeaderProps> = ({
  clientName,
  consentLevel,
  onBack,
  t,
}) => {
  const consentLabelKey = CONSENT_LABEL_KEYS[consentLevel];
  const consentLabel = t(consentLabelKey as never);

  return (
    <View style={styles.header}>
      <Pressable
        style={({ pressed }) => [styles.backButton, pressed && styles.backButtonPressed]}
        onPress={onBack}
        hitSlop={HIT_SLOP}
        accessibilityRole="button"
        accessibilityLabel={t('client_view.back')}
      >
        <Ionicons name="arrow-back" size={20} color="#52525B" />
      </Pressable>

      <Text style={styles.title} numberOfLines={1}>
        {clientName}
      </Text>

      <View
        style={styles.consentPill}
        accessibilityElementsHidden={false}
        accessible={true}
        accessibilityLabel={t('client_view.consent_level' as never, { level: consentLabel })}
      >
        <Ionicons name="eye-outline" size={12} color="#0D9488" />
        <Text style={styles.consentText}>{consentLabel}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 12,
  } as ViewStyle,
  backButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 22,
    flexShrink: 0,
  },
  backButtonPressed: {
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
  },
  title: {
    flex: 1,
    fontSize: 20,
    fontWeight: '800',
    color: '#18181B',
    letterSpacing: -0.4,
  },
  consentPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 9999,
    backgroundColor: 'rgba(20, 184, 166, 0.12)',
    flexShrink: 0,
  },
  consentText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#0D9488',
  },
});
